import { useQuery } from "@tanstack/react-query";

import { api, type Notification } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";

const SEVERITY: Record<string, { rule: string; pill: string; glow: string }> = {
  critical: {
    rule: "rule-crit",
    pill: "bg-crit/15 text-crit-400 border-crit/40",
    glow: "text-glow-crit text-crit-400",
  },
  high: {
    rule: "rule-crit",
    pill: "bg-crit/15 text-crit-400 border-crit/40",
    glow: "text-glow-crit text-crit-400",
  },
  medium: {
    rule: "rule-warn",
    pill: "bg-warn/15 text-warn-400 border-warn/40",
    glow: "text-glow-warn text-warn-400",
  },
  low: {
    rule: "rule-info",
    pill: "bg-cyber/15 text-cyber-400 border-cyber/40",
    glow: "text-glow-cyber text-cyber-400",
  },
};

const TYPE_LABEL: Record<string, string> = {
  high_severity_alert: "High-severity finding",
  report_ready: "Report ready",
  pause_started: "Pause started",
  pause_pre_resume: "Pause pre-resume reminder",
  incident_advisory: "Incident advisory",
  all_clear: "All-clear",
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

export function NotificationsPage(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ["notifications"],
    queryFn: api.notifications,
    refetchInterval: 10000,
  });

  if (isLoading) return <main className="text-chrome-300">Loading…</main>;
  if (error || !data) return <main className="text-crit">Failed to load.</main>;

  const items: Notification[] = data.notifications;

  return (
    <main>
      <PageHeader
        eyebrow="inbox"
        title="Notifications"
        subtitle="High-severity findings, report-ready pings, pause / incident advisories."
      />

      {items.length === 0 ? (
        <div className="panel p-10 text-center text-sm text-chrome-400">
          No notifications yet. High-severity findings and report-ready events will land here.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((n) => {
            const sev = (n.severity ? SEVERITY[n.severity] : SEVERITY.low) ?? SEVERITY.low!;
            const typeLabel = TYPE_LABEL[n.type] ?? n.type;
            return (
              <li
                key={n.id}
                className={["panel relative overflow-hidden py-5 pl-6 pr-5", sev.rule].join(
                  " ",
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={[
                      "rounded border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest",
                      sev.pill,
                    ].join(" ")}
                  >
                    {n.severity ?? "info"}
                  </span>
                  <span className="text-[10px] font-medium uppercase tracking-widest text-chrome-400">
                    {typeLabel}
                  </span>
                  <span className="ml-auto text-[11px] text-chrome-400">
                    {timeAgo(n.created_at)}
                  </span>
                </div>
                <h2 className="mt-3 text-base font-semibold text-chrome-50">{n.title}</h2>
                {n.plain_summary && (
                  <p className="mt-1 max-w-3xl text-sm leading-relaxed text-chrome-300">
                    {n.plain_summary}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
